import { createReducer, on } from '@ngrx/store';
import { ILiabilitiesState } from './liabilities.state';
import {
  documentTypesLoaded,
  getLiability,
  getLiabilityFailure,
  getLiabilitySuccess,
  initializeLiabilityPage,
  loadDamageWithFiles,
  loadDamageWithFilesFailure,
  loadDamageWithFilesSuccess,
  loadExistingDamages,
  loadExistingDamagesFailure,
  loadExistingDamagesSuccess,
  loadReturnDamages,
  loadReturnDamagesFailure,
  loadReturnDamagesSuccess,
  loadThirdPartyDamages,
  loadThirdPartyDamagesFailure,
  loadThirdPartyDamagesSuccess,
  loadTransportationStages,
  loadTransportationStagesFailure,
  loadTransportationStagesSuccess,
  updateLiabilityState,
  updateLiabilityStateFailure,
  updateLiabilityStateSuccess,
  uploadNewFile,
  uploadNewFileFailure,
  uploadNewFileSuccess,
  deleteFile,
  deleteFileFailure,
  deleteFileSuccess,
} from './liabilities.actions';

export const liabilitiesInitialState: ILiabilitiesState = {
  currentLiability: null,
  currentDamage: null,
  loading: false,
  pendingActions: 0,
  completedActions: 0,
  returnDamages: null,
  returnDamagesLoading: false,
  existingDamages: null,
  existingDamagesLoading: false,
  thirdPartyDamages: null,
  thirdPartyDamagesLoading: false,
  transportationStageOptions: null,
  updateLoading: false,
  uploadLoading: false,
  deleteLoading: false,
};

export const liabilitiesReducer = createReducer(
  liabilitiesInitialState,
  on(initializeLiabilityPage, (state) => {
    return {
      ...state,
      loading: true,
      pendingActions: 3, // Three actions: getLiability, loadDamageWithFiles, loadDocumentTypes
      completedActions: 0,
    };
  }),
  on(getLiability, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(getLiabilitySuccess, (state, { liability }) => {
    const completedActions = state.completedActions + 1;
    return {
      ...state,
      currentLiability: liability,
      completedActions,
      loading: completedActions < state.pendingActions,
    };
  }),
  on(getLiabilityFailure, (state) => {
    const completedActions = state.completedActions + 1;
    return {
      ...state,
      currentLiability: null,
      completedActions,
      loading: completedActions < state.pendingActions,
    };
  }),
  on(updateLiabilityState, (state) => ({ ...state, updateLoading: true })),
  on(updateLiabilityStateFailure, (state) => ({
    ...state,
    updateLoading: false,
  })),
  on(
    updateLiabilityStateSuccess,
    (state, { comment, status, cycles, isProcessEnd }) => {
      if (!state.currentLiability) {
        return { ...state, loading: false, updateLoading: false };
      }
      return {
        ...state,
        currentLiability: {
          ...state.currentLiability,
          liabilityDetails: {
            ...state.currentLiability.liabilityDetails,
            comment,
            status,
            cycles,
            isProcessEnd,
          },
        },
        loading: false,
        updateLoading: false,
      };
    }
  ),
  on(loadDamageWithFiles, (state) => ({
    ...state,
    loading: true,
  })),
  on(loadDamageWithFilesSuccess, (state, damage) => {
    const completedActions = state.completedActions + 1;
    return {
      ...state,
      currentDamage: damage,
      completedActions,
      loading: completedActions < state.pendingActions,
    };
  }),
  on(loadDamageWithFilesFailure, (state) => {
    const completedActions = state.completedActions + 1;
    return {
      ...state,
      completedActions,
      loading: completedActions < state.pendingActions,
    };
  }),
  on(documentTypesLoaded, (state) => {
    const completedActions = state.completedActions + 1;
    return {
      ...state,
      completedActions,
      loading: completedActions < state.pendingActions,
    };
  }),
  on(loadReturnDamages, (state) => ({
    ...state,
    returnDamages: null,
    returnDamagesLoading: true,
  })),
  on(loadReturnDamagesSuccess, (state, { damages }) => ({
    ...state,
    returnDamages: damages,
    returnDamagesLoading: false,
  })),
  on(loadReturnDamagesFailure, (state) => ({
    ...state,
    returnDamagesLoading: false,
  })),
  on(loadExistingDamages, (state) => ({
    ...state,
    existingDamages: null,
    existingDamagesLoading: true,
  })),
  on(loadExistingDamagesSuccess, (state, { damages }) => ({
    ...state,
    existingDamages: damages,
    existingDamagesLoading: false,
  })),
  on(loadExistingDamagesFailure, (state) => ({
    ...state,
    existingDamagesLoading: false,
  })),
  on(uploadNewFile, (state) => ({ ...state, uploadLoading: true })),
  on(uploadNewFileSuccess, (state) => ({ ...state, uploadLoading: false })),
  on(uploadNewFileFailure, (state) => ({ ...state, uploadLoading: false })),
  on(deleteFile, (state) => ({ ...state, deleteLoading: true })),
  on(deleteFileSuccess, (state) => ({ ...state, deleteLoading: false })),
  on(deleteFileFailure, (state) => ({ ...state, deleteLoading: false })),
  on(loadThirdPartyDamages, (state) => ({
    ...state,
    thirdPartyDamages: null,
    thirdPartyDamagesLoading: true,
  })),
  on(loadThirdPartyDamagesSuccess, (state, { damages }) => ({
    ...state,
    thirdPartyDamages: damages,
    thirdPartyDamagesLoading: false,
  })),
  on(loadThirdPartyDamagesFailure, (state) => ({
    ...state,
    thirdPartyDamages: [],
    thirdPartyDamagesLoading: false,
  })),
  on(loadTransportationStages, (state) => ({
    ...state,
    transportationStageOptions: null,
  })),
  on(loadTransportationStagesSuccess, (state, { options }) => ({
    ...state,
    transportationStageOptions: options,
  })),
  on(loadTransportationStagesFailure, (state) => ({
    ...state,
    transportationStageOptions: [],
  }))
);
